/**
 * Migration utility for questionnaire templates
 * 
 * This module provides a browser-compatible migration function that can be
 * called from the application or browser console to populate Firebase with
 * questionnaire templates.
 * 
 * Usage in browser console:
 *   import { runMigration } from './utils/migrateQuestionnaireTemplates';
 *   runMigration();
 * 
 * Or add a temporary button in your app to trigger this.
 */

import {
  collection,
  doc,
  setDoc,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { LoanType } from '@/types';
import type {
  QuestionnaireTemplate,
  TemplateSection,
  TemplateField,
  FieldType,
} from '@/types/questionnaire';

// ============================================================================
// TEMPLATE DATA
// ============================================================================

const getLoanTypeName = (loanType: LoanType): string => {
  const names: Record<LoanType, string> = {
    home_loan: 'Home Loan',
    lap: 'Loan Against Property',
    personal_loan: 'Personal Loan',
    business_loan: 'Business Loan',
    car_loan: 'Car Loan',
  };
  return names[loanType];
};

// Home Loan / LAP Template
const homeLoanSections: TemplateSection[] = [
  {
    id: 'property_details',
    title: 'Property Details',
    description: 'Tell us about the property you want to finance',
    iconName: 'Home',
    order: 0,
    fields: [
      { id: 'propertyType', type: 'radio', label: 'Property Type', order: 0, options: [
        { value: 'residential_apartment', label: 'Residential Apartment' },
        { value: 'independent_house', label: 'Independent House' },
        { value: 'villa', label: 'Villa' },
        { value: 'plot', label: 'Plot/Land' },
        { value: 'commercial', label: 'Commercial Property' }
      ], validation: { required: true } },
      { id: 'propertyStatus', type: 'radio', label: 'Property Status', order: 1, options: [
        { value: 'ready_to_move', label: 'Ready to Move' },
        { value: 'under_construction', label: 'Under Construction' },
        { value: 'land', label: 'Land/Plot Only' }
      ], validation: { required: true } },
      { id: 'propertyValue', type: 'currency', label: 'Property Value (₹)', placeholder: 'Ex - 50,00,000', order: 2, validation: { required: true, min: 100000 } },
      { id: 'propertyAge', type: 'number', label: 'Property Age (Years)', placeholder: 'Ex - 5', order: 3,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'propertyStatus', operator: 'not_equals', value: 'land' }] }
      },
      { id: 'propertyAddress', type: 'textarea', label: 'Property Address', placeholder: 'Complete address with landmark', order: 4, validation: { required: true, minLength: 10 } },
      { id: 'propertyCity', type: 'text', label: 'Property City', placeholder: 'Ex - Mumbai', order: 5, validation: { required: true } },
      { id: 'propertyPincode', type: 'text', label: 'Property Pincode', placeholder: 'Ex - 400001', order: 6, validation: { required: true, pattern: '^[0-9]{6}$' } },
      { id: 'builtUpArea', type: 'number', label: 'Built-up Area (sq.ft)', placeholder: 'Ex - 1200', order: 7, validation: { min: 100 },
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'propertyStatus', operator: 'not_equals', value: 'land' }] }
      },
      { id: 'plotArea', type: 'number', label: 'Plot Area (sq.ft)', placeholder: 'Ex - 1500', order: 8, validation: { min: 100 },
        conditionalLogic: { action: 'show', logicType: 'or', rules: [
          { fieldId: 'propertyType', operator: 'equals', value: 'plot' },
          { fieldId: 'propertyType', operator: 'equals', value: 'independent_house' }
        ] }
      },
    ]
  },
  {
    id: 'purchase_details',
    title: 'Purchase/Construction Details',
    description: 'Details about your purchase or construction plans',
    iconName: 'Building2',
    order: 1,
    fields: [
      { id: 'loanPurpose', type: 'radio', label: 'Loan Purpose', order: 0, options: [
        { value: 'purchase', label: 'Purchase of Property' },
        { value: 'construction', label: 'Construction on Own Land' },
        { value: 'renovation', label: 'Renovation/Extension' },
        { value: 'lap', label: 'Loan Against Property' }
      ], validation: { required: true } },
      { id: 'sellerName', type: 'text', label: 'Seller/Builder Name', placeholder: 'Ex - ABC Builders Pvt Ltd', order: 1,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'loanPurpose', operator: 'equals', value: 'purchase' }] }
      },
      { id: 'sellerPAN', type: 'text', label: 'Seller/Builder PAN', placeholder: 'Ex - ABCDE1234F', order: 2, validation: { pattern: '^[A-Z]{5}[0-9]{4}[A-Z]{1}$' },
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'loanPurpose', operator: 'equals', value: 'purchase' }] }
      },
      { id: 'registryDate', type: 'date', label: 'Expected Registry Date', order: 3,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'loanPurpose', operator: 'equals', value: 'purchase' }] }
      },
      { id: 'ownContribution', type: 'currency', label: 'Own Contribution/Down Payment (₹)', placeholder: 'Ex - 10,00,000', order: 4, validation: { required: true, min: 0 } },
      { id: 'loanTenureRequired', type: 'number', label: 'Loan Tenure Required (Years)', placeholder: 'Ex - 20', order: 5, validation: { required: true, min: 5, max: 30 } },
    ]
  },
  {
    id: 'co_applicant',
    title: 'Co-Applicant Details',
    description: 'Add co-applicant to increase loan eligibility',
    iconName: 'Users',
    order: 2,
    fields: [
      { id: 'hasCoApplicant', type: 'radio', label: 'Do you have a co-applicant?', order: 0, options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ], validation: { required: true } },
      { id: 'coApplicantName', type: 'text', label: 'Co-Applicant Full Name', placeholder: 'Ex - Rajesh Kumar', order: 1, validation: { required: true },
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'hasCoApplicant', operator: 'equals', value: 'yes' }] }
      },
      { id: 'coApplicantRelation', type: 'select', label: 'Relationship with Applicant', order: 2, validation: { required: true }, options: [
        { value: 'spouse', label: 'Spouse' },
        { value: 'parent', label: 'Parent' },
        { value: 'sibling', label: 'Sibling' },
        { value: 'child', label: 'Child' },
        { value: 'other', label: 'Other' }
      ],
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'hasCoApplicant', operator: 'equals', value: 'yes' }] }
      },
      { id: 'coApplicantDOB', type: 'date', label: 'Co-Applicant Date of Birth', order: 3, validation: { required: true },
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'hasCoApplicant', operator: 'equals', value: 'yes' }] }
      },
      { id: 'coApplicantPAN', type: 'text', label: 'Co-Applicant PAN Card', placeholder: 'Ex - ABCDE1234F', order: 4, validation: { pattern: '^[A-Z]{5}[0-9]{4}[A-Z]{1}$' },
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'hasCoApplicant', operator: 'equals', value: 'yes' }] }
      },
      { id: 'coApplicantEmployment', type: 'select', label: 'Co-Applicant Employment Type', order: 5, options: [
        { value: 'salaried', label: 'Salaried' },
        { value: 'self_employed_professional', label: 'Self-Employed Professional' },
        { value: 'self_employed_business', label: 'Self-Employed Business' },
        { value: 'retired', label: 'Retired' },
        { value: 'homemaker', label: 'Homemaker' }
      ],
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'hasCoApplicant', operator: 'equals', value: 'yes' }] }
      },
      { id: 'coApplicantIncome', type: 'currency', label: 'Co-Applicant Monthly Income (₹)', placeholder: 'Ex - 40,000', order: 6,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [
          { fieldId: 'hasCoApplicant', operator: 'equals', value: 'yes' },
          { fieldId: 'coApplicantEmployment', operator: 'in', value: ['salaried', 'self_employed_professional', 'self_employed_business'] }
        ] }
      },
    ]
  },
  {
    id: 'employment_income',
    title: 'Income & Employment',
    description: 'Your employment and income details',
    iconName: 'Briefcase',
    order: 3,
    fields: [
      { id: 'employmentType', type: 'radio', label: 'Employment Type', order: 0, options: [
        { value: 'salaried', label: 'Salaried' },
        { value: 'self_employed_professional', label: 'Self-Employed Professional' },
        { value: 'self_employed_business', label: 'Self-Employed Business' }
      ], validation: { required: true } },
      // Salaried fields
      { id: 'companyName', type: 'text', label: 'Company Name', placeholder: 'Ex - Infosys Ltd', order: 1, validation: { required: true },
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'employmentType', operator: 'equals', value: 'salaried' }] }
      },
      { id: 'designation', type: 'text', label: 'Designation', placeholder: 'Ex - Senior Manager', order: 2, validation: { required: true },
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'employmentType', operator: 'equals', value: 'salaried' }] }
      },
      { id: 'companyType', type: 'select', label: 'Company Type', order: 3, options: [
        { value: 'mnc', label: 'MNC' },
        { value: 'public_sector', label: 'Public Sector/Govt' },
        { value: 'private_limited', label: 'Private Limited' },
        { value: 'partnership', label: 'Partnership' },
        { value: 'proprietorship', label: 'Proprietorship' }
      ],
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'employmentType', operator: 'equals', value: 'salaried' }] }
      },
      { id: 'dateOfJoining', type: 'date', label: 'Date of Joining Current Company', order: 4, validation: { required: true },
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'employmentType', operator: 'equals', value: 'salaried' }] }
      },
      { id: 'totalExperience', type: 'number', label: 'Total Work Experience (Years)', placeholder: 'Ex - 8', order: 5, validation: { min: 0, max: 50 },
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'employmentType', operator: 'equals', value: 'salaried' }] }
      },
      { id: 'officeAddress', type: 'textarea', label: 'Office Address', placeholder: 'Complete office address', order: 6,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'employmentType', operator: 'equals', value: 'salaried' }] }
      },
      // Self-employed fields
      { id: 'businessName', type: 'text', label: 'Business/Firm Name', placeholder: 'Ex - ABC Enterprises', order: 7, validation: { required: true },
        conditionalLogic: { action: 'show', logicType: 'or', rules: [
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_professional' },
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_business' }
        ] }
      },
      { id: 'natureOfBusiness', type: 'text', label: 'Nature of Business', placeholder: 'Ex - Textile Trading', order: 8, validation: { required: true },
        conditionalLogic: { action: 'show', logicType: 'or', rules: [
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_professional' },
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_business' }
        ] }
      },
      { id: 'businessVintage', type: 'number', label: 'Business Vintage (Years)', placeholder: 'Ex - 5', order: 9, validation: { required: true, min: 0 },
        conditionalLogic: { action: 'show', logicType: 'or', rules: [
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_professional' },
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_business' }
        ] }
      },
      { id: 'businessAddress', type: 'textarea', label: 'Business Address', placeholder: 'Complete business address', order: 10,
        conditionalLogic: { action: 'show', logicType: 'or', rules: [
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_professional' },
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_business' }
        ] }
      },
      { id: 'gstNumber', type: 'text', label: 'GST Number (if applicable)', placeholder: 'Ex - 27XXXXX1234X1Z5', order: 11,
        conditionalLogic: { action: 'show', logicType: 'or', rules: [
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_professional' },
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_business' }
        ] }
      },
      // Common income fields
      { id: 'monthlyIncome', type: 'currency', label: 'Monthly Net Income/Salary (₹)', placeholder: 'Ex - 75,000', order: 12, validation: { required: true, min: 10000 } },
      { id: 'annualIncome', type: 'currency', label: 'Annual Gross Income (₹)', placeholder: 'Ex - 12,00,000', order: 13, validation: { required: true, min: 100000 } },
      { id: 'otherIncome', type: 'currency', label: 'Other Income (Rental/Interest) (₹)', placeholder: 'Ex - 15,000', order: 14 },
    ]
  },
  {
    id: 'existing_liabilities',
    title: 'Existing Loans & Liabilities',
    description: 'Details of your current EMIs and obligations',
    iconName: 'CreditCard',
    order: 4,
    fields: [
      { id: 'hasExistingLoans', type: 'radio', label: 'Do you have any existing loans/EMIs?', order: 0, options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ], validation: { required: true } },
      { id: 'homeLoanEMI', type: 'currency', label: 'Existing Home Loan EMI (₹)', placeholder: 'Ex - 25,000', order: 1,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'hasExistingLoans', operator: 'equals', value: 'yes' }] }
      },
      { id: 'personalLoanEMI', type: 'currency', label: 'Personal Loan EMI (₹)', placeholder: 'Ex - 10,000', order: 2,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'hasExistingLoans', operator: 'equals', value: 'yes' }] }
      },
      { id: 'carLoanEMI', type: 'currency', label: 'Car/Vehicle Loan EMI (₹)', placeholder: 'Ex - 15,000', order: 3,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'hasExistingLoans', operator: 'equals', value: 'yes' }] }
      },
      { id: 'creditCardEMI', type: 'currency', label: 'Credit Card EMI (₹)', placeholder: 'Ex - 5,000', order: 4,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'hasExistingLoans', operator: 'equals', value: 'yes' }] }
      },
      { id: 'otherLoans', type: 'textarea', label: 'Other Loans (Describe)', placeholder: 'Business loan, Education loan, etc.', order: 5,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'hasExistingLoans', operator: 'equals', value: 'yes' }] }
      },
      { id: 'totalMonthlyEMI', type: 'currency', label: 'Total Monthly EMI (₹)', placeholder: 'Ex - 55,000', order: 6,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'hasExistingLoans', operator: 'equals', value: 'yes' }] }
      },
    ]
  },
  {
    id: 'banking_details',
    title: 'Banking & Financial Details',
    description: 'Your primary banking relationship',
    iconName: 'Building',
    order: 5,
    fields: [
      { id: 'primaryBankName', type: 'text', label: 'Primary Bank Name', placeholder: 'Ex - HDFC Bank', order: 0, validation: { required: true } },
      { id: 'accountType', type: 'select', label: 'Account Type', order: 1, options: [
        { value: 'savings', label: 'Savings Account' },
        { value: 'current', label: 'Current Account' },
        { value: 'salary', label: 'Salary Account' }
      ], validation: { required: true } },
      { id: 'accountVintage', type: 'number', label: 'Account Age (Years)', placeholder: 'Ex - 5', order: 2, validation: { required: true, min: 0 } },
      { id: 'monthlyBankingTurnover', type: 'currency', label: 'Average Monthly Banking (₹)', placeholder: 'Ex - 2,00,000', order: 3,
        conditionalLogic: { action: 'show', logicType: 'or', rules: [
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_professional' },
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_business' }
        ] }
      },
      { id: 'itrFiled', type: 'radio', label: 'ITR Filed for Last 2 Years?', order: 4, options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ], validation: { required: true } },
      { id: 'last2YearsITR', type: 'textarea', label: 'Last 2 Years ITR Amount (₹)', placeholder: 'Ex - 2023: 10,00,000\n2024: 12,00,000', order: 5,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'itrFiled', operator: 'equals', value: 'yes' }] }
      },
    ]
  },
  {
    id: 'documents_checklist',
    title: 'Required Documents',
    description: 'Checklist of documents needed for loan processing',
    iconName: 'FileCheck',
    order: 6,
    fields: [
      { id: 'documentsChecklist', type: 'checkbox', label: 'Check documents you have ready', order: 0, options: [
        { value: 'pan_card', label: 'PAN Card' },
        { value: 'aadhar_card', label: 'Aadhar Card' },
        { value: 'bank_statements_6m', label: 'Bank Statements (Last 6 months)' },
        { value: 'salary_slips_3m', label: 'Salary Slips (Last 3 months)' },
        { value: 'form16', label: 'Form 16' },
        { value: 'itr_2years', label: 'ITR (Last 2 years)' },
        { value: 'property_papers', label: 'Property Documents' },
        { value: 'sale_agreement', label: 'Sale Agreement' },
        { value: 'builder_agreement', label: 'Builder Agreement' },
        { value: 'business_proof', label: 'Business Proof (GST/Shop License)' },
        { value: 'property_tax_receipt', label: 'Property Tax Receipt' }
      ] },
    ]
  },
];

// Personal Loan Template
const personalLoanSections: TemplateSection[] = [
  {
    id: 'loan_purpose',
    title: 'Loan Purpose',
    description: 'What is the loan for?',
    iconName: 'Target',
    order: 0,
    fields: [
      { id: 'loanPurpose', type: 'radio', label: 'What is the loan for?', order: 0, options: [
        { value: 'debt_consolidation', label: 'Debt Consolidation' },
        { value: 'medical_emergency', label: 'Medical Emergency' },
        { value: 'wedding', label: 'Wedding Expenses' },
        { value: 'home_renovation', label: 'Home Renovation' },
        { value: 'travel', label: 'Travel/Vacation' },
        { value: 'business', label: 'Business Purpose' },
        { value: 'education', label: 'Education' },
        { value: 'other', label: 'Other' }
      ], validation: { required: true } },
      { id: 'loanPurposeDetails', type: 'textarea', label: 'Provide details', placeholder: 'Brief description of loan purpose', order: 1, validation: { required: true, minLength: 10 } },
      { id: 'loanAmountRequired', type: 'currency', label: 'Loan Amount Required (₹)', placeholder: 'Ex - 5,00,000', order: 2, validation: { required: true, min: 50000 } },
      { id: 'loanTenureRequired', type: 'number', label: 'Loan Tenure Required (Months)', placeholder: 'Ex - 36', order: 3, validation: { required: true, min: 12, max: 60 } },
    ]
  },
  {
    id: 'employment_income',
    title: 'Employment & Income',
    description: 'Your employment and income details',
    iconName: 'Briefcase',
    order: 1,
    fields: [
      { id: 'employmentType', type: 'radio', label: 'Employment Type', order: 0, options: [
        { value: 'salaried', label: 'Salaried' },
        { value: 'self_employed_professional', label: 'Self-Employed Professional' },
        { value: 'self_employed_business', label: 'Self-Employed Business' }
      ], validation: { required: true } },
      { id: 'companyName', type: 'text', label: 'Company Name', placeholder: 'Ex - Infosys Ltd', order: 1, validation: { required: true },
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'employmentType', operator: 'equals', value: 'salaried' }] }
      },
      { id: 'designation', type: 'text', label: 'Designation', placeholder: 'Ex - Senior Manager', order: 2, validation: { required: true },
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'employmentType', operator: 'equals', value: 'salaried' }] }
      },
      { id: 'dateOfJoining', type: 'date', label: 'Date of Joining Current Company', order: 3, validation: { required: true },
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'employmentType', operator: 'equals', value: 'salaried' }] }
      },
      { id: 'businessName', type: 'text', label: 'Business/Firm Name', placeholder: 'Ex - ABC Enterprises', order: 4, validation: { required: true },
        conditionalLogic: { action: 'show', logicType: 'or', rules: [
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_professional' },
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_business' }
        ] }
      },
      { id: 'businessVintage', type: 'number', label: 'Business Vintage (Years)', placeholder: 'Ex - 3', order: 5, validation: { required: true, min: 1 },
        conditionalLogic: { action: 'show', logicType: 'or', rules: [
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_professional' },
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_business' }
        ] }
      },
      { id: 'monthlyIncome', type: 'currency', label: 'Monthly Net Salary (₹)', placeholder: 'Ex - 50,000', order: 6, validation: { required: true, min: 15000 } },
    ]
  },
  {
    id: 'existing_liabilities',
    title: 'Existing Obligations',
    description: 'Your current EMIs',
    iconName: 'CreditCard',
    order: 2,
    fields: [
      { id: 'hasExistingLoans', type: 'radio', label: 'Do you have any existing EMIs?', order: 0, options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ], validation: { required: true } },
      { id: 'totalMonthlyEMI', type: 'currency', label: 'Total Monthly EMI (₹)', placeholder: 'Ex - 20,000', order: 1,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'hasExistingLoans', operator: 'equals', value: 'yes' }] }
      },
    ]
  },
  {
    id: 'documents_checklist',
    title: 'Required Documents',
    description: 'Checklist of documents needed',
    iconName: 'FileCheck',
    order: 3,
    fields: [
      { id: 'documentsChecklist', type: 'checkbox', label: 'Check documents you have ready', order: 0, options: [
        { value: 'pan_card', label: 'PAN Card' },
        { value: 'aadhar_card', label: 'Aadhar Card' },
        { value: 'bank_statements_6m', label: 'Bank Statements (Last 6 months)' },
        { value: 'salary_slips_3m', label: 'Salary Slips (Last 3 months)' },
        { value: 'itr_2years', label: 'ITR (Last 2 years)' },
      ] },
    ]
  },
];

// Business Loan Template
const businessLoanSections: TemplateSection[] = [
  {
    id: 'business_details',
    title: 'Business Details',
    description: 'Tell us about your business',
    iconName: 'Building2',
    order: 0,
    fields: [
      { id: 'businessName', type: 'text', label: 'Business Name', placeholder: 'Ex - ABC Enterprises', order: 0, validation: { required: true } },
      { id: 'businessType', type: 'select', label: 'Business Type', order: 1, options: [
        { value: 'proprietorship', label: 'Proprietorship' },
        { value: 'partnership', label: 'Partnership' },
        { value: 'pvt_ltd', label: 'Private Limited' },
        { value: 'llp', label: 'LLP' },
        { value: 'public_ltd', label: 'Public Limited' }
      ], validation: { required: true } },
      { id: 'natureOfBusiness', type: 'text', label: 'Nature of Business', placeholder: 'Ex - Textile Trading', order: 2, validation: { required: true } },
      { id: 'businessVintage', type: 'number', label: 'Business Vintage (Years)', placeholder: 'Ex - 5', order: 3, validation: { required: true, min: 1 } },
      { id: 'gstNumber', type: 'text', label: 'GST Number', placeholder: 'Ex - 27XXXXX1234X1Z5', order: 4 },
      { id: 'businessAddress', type: 'textarea', label: 'Business Address', placeholder: 'Complete business address', order: 5, validation: { required: true } },
    ]
  },
  {
    id: 'loan_requirement',
    title: 'Loan Requirement',
    description: 'Your loan requirements',
    iconName: 'Target',
    order: 1,
    fields: [
      { id: 'loanPurpose', type: 'radio', label: 'Purpose of Loan', order: 0, options: [
        { value: 'working_capital', label: 'Working Capital' },
        { value: 'business_expansion', label: 'Business Expansion' },
        { value: 'machinery', label: 'Machinery/Equipment' },
        { value: 'stock_purchase', label: 'Stock Purchase' },
        { value: 'other', label: 'Other' }
      ], validation: { required: true } },
      { id: 'loanAmountRequired', type: 'currency', label: 'Loan Amount Required (₹)', placeholder: 'Ex - 25,00,000', order: 1, validation: { required: true, min: 100000 } },
      { id: 'loanTenureRequired', type: 'number', label: 'Loan Tenure (Months)', placeholder: 'Ex - 36', order: 2, validation: { required: true, min: 12, max: 84 } },
    ]
  },
  {
    id: 'financials',
    title: 'Business Financials',
    description: 'Your business financial details',
    iconName: 'TrendingUp',
    order: 2,
    fields: [
      { id: 'annualTurnover', type: 'currency', label: 'Annual Turnover (₹)', placeholder: 'Ex - 2,00,00,000', order: 0, validation: { required: true, min: 1000000 } },
      { id: 'annualProfit', type: 'currency', label: 'Annual Net Profit (₹)', placeholder: 'Ex - 30,00,000', order: 1, validation: { required: true } },
      { id: 'monthlyBanking', type: 'currency', label: 'Average Monthly Banking (₹)', placeholder: 'Ex - 15,00,000', order: 2 },
      { id: 'itrFiled', type: 'radio', label: 'ITR Filed for Last 2-3 Years?', order: 3, options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ], validation: { required: true } },
    ]
  },
  {
    id: 'documents_checklist',
    title: 'Required Documents',
    description: 'Checklist of documents needed',
    iconName: 'FileCheck',
    order: 3,
    fields: [
      { id: 'documentsChecklist', type: 'checkbox', label: 'Check documents you have ready', order: 0, options: [
        { value: 'pan_card', label: 'PAN Card' },
        { value: 'aadhar_card', label: 'Aadhar Card' },
        { value: 'bank_statements_12m', label: 'Bank Statements (Last 12 months)' },
        { value: 'itr_2years', label: 'ITR (Last 2-3 years)' },
        { value: 'gst_returns', label: 'GST Returns' },
        { value: 'business_proof', label: 'Business Proof (Registration/License)' },
        { value: 'financial_statements', label: 'Financial Statements' },
      ] },
    ]
  },
];

// Car Loan Template
const carLoanSections: TemplateSection[] = [
  {
    id: 'vehicle_details',
    title: 'Vehicle Details',
    description: 'Details about the vehicle',
    iconName: 'Car',
    order: 0,
    fields: [
      { id: 'vehicleType', type: 'radio', label: 'Vehicle Type', order: 0, options: [
        { value: 'new', label: 'New Vehicle' },
        { value: 'used', label: 'Used Vehicle' }
      ], validation: { required: true } },
      { id: 'vehicleCategory', type: 'select', label: 'Vehicle Category', order: 1, options: [
        { value: 'car', label: 'Car' },
        { value: 'suv', label: 'SUV' },
        { value: 'two_wheeler', label: 'Two Wheeler' },
        { value: 'commercial', label: 'Commercial Vehicle' }
      ], validation: { required: true } },
      { id: 'vehicleBrand', type: 'text', label: 'Brand', placeholder: 'Ex - Maruti Suzuki', order: 2, validation: { required: true } },
      { id: 'vehicleModel', type: 'text', label: 'Model', placeholder: 'Ex - Swift Dzire', order: 3, validation: { required: true } },
      { id: 'vehicleYear', type: 'number', label: 'Manufacturing Year', placeholder: 'Ex - 2024', order: 4, validation: { required: true, min: 2010 },
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'vehicleType', operator: 'equals', value: 'used' }] }
      },
      { id: 'vehicleValue', type: 'currency', label: 'Vehicle Value (₹)', placeholder: 'Ex - 10,00,000', order: 5, validation: { required: true, min: 100000 } },
      { id: 'downPayment', type: 'currency', label: 'Down Payment (₹)', placeholder: 'Ex - 2,00,000', order: 6, validation: { required: true, min: 0 } },
      { id: 'loanTenureRequired', type: 'number', label: 'Loan Tenure (Months)', placeholder: 'Ex - 60', order: 7, validation: { required: true, min: 12, max: 84 } },
    ]
  },
  {
    id: 'employment_income',
    title: 'Employment & Income',
    description: 'Your employment and income details',
    iconName: 'Briefcase',
    order: 1,
    fields: [
      { id: 'employmentType', type: 'radio', label: 'Employment Type', order: 0, options: [
        { value: 'salaried', label: 'Salaried' },
        { value: 'self_employed_professional', label: 'Self-Employed Professional' },
        { value: 'self_employed_business', label: 'Self-Employed Business' }
      ], validation: { required: true } },
      { id: 'companyName', type: 'text', label: 'Company Name', placeholder: 'Ex - Infosys Ltd', order: 1,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'employmentType', operator: 'equals', value: 'salaried' }] }
      },
      { id: 'businessName', type: 'text', label: 'Business Name', placeholder: 'Ex - ABC Enterprises', order: 2,
        conditionalLogic: { action: 'show', logicType: 'or', rules: [
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_professional' },
          { fieldId: 'employmentType', operator: 'equals', value: 'self_employed_business' }
        ] }
      },
      { id: 'monthlyIncome', type: 'currency', label: 'Monthly Income (₹)', placeholder: 'Ex - 50,000', order: 3, validation: { required: true, min: 15000 } },
    ]
  },
  {
    id: 'existing_liabilities',
    title: 'Existing Obligations',
    description: 'Your current EMIs',
    iconName: 'CreditCard',
    order: 2,
    fields: [
      { id: 'hasExistingLoans', type: 'radio', label: 'Do you have any existing EMIs?', order: 0, options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ], validation: { required: true } },
      { id: 'totalMonthlyEMI', type: 'currency', label: 'Total Monthly EMI (₹)', placeholder: 'Ex - 15,000', order: 1,
        conditionalLogic: { action: 'show', logicType: 'and', rules: [{ fieldId: 'hasExistingLoans', operator: 'equals', value: 'yes' }] }
      },
    ]
  },
  {
    id: 'documents_checklist',
    title: 'Required Documents',
    description: 'Checklist of documents needed',
    iconName: 'FileCheck',
    order: 3,
    fields: [
      { id: 'documentsChecklist', type: 'checkbox', label: 'Check documents you have ready', order: 0, options: [
        { value: 'pan_card', label: 'PAN Card' },
        { value: 'aadhar_card', label: 'Aadhar Card' },
        { value: 'bank_statements_6m', label: 'Bank Statements (Last 6 months)' },
        { value: 'salary_slips_3m', label: 'Salary Slips (Last 3 months)' },
        { value: 'itr_2years', label: 'ITR (Last 2 years)' },
        { value: 'proforma_invoice', label: 'Proforma Invoice' },
      ] },
    ]
  },
];

// ============================================================================
// MIGRATION FUNCTION
// ============================================================================

interface MigrationResult {
  success: boolean;
  templatesCreated: string[];
  errors: { loanType: string; error: string }[];
}

/**
 * Run the migration to create questionnaire templates in Firebase
 */
export const runMigration = async (): Promise<MigrationResult> => {
  const result: MigrationResult = {
    success: true,
    templatesCreated: [],
    errors: [],
  };

  console.log('🚀 Starting questionnaire template migration...\n');

  const templates: { loanType: LoanType; sections: TemplateSection[]; requiredDocuments: string[] }[] = [
    {
      loanType: 'home_loan',
      sections: homeLoanSections,
      requiredDocuments: ['PAN Card', 'Aadhar Card', 'Bank Statements (6 months)', 'Salary Slips (3 months) / ITR (2 years)', 'Property Documents', 'Sale Agreement'],
    },
    {
      loanType: 'lap',
      sections: homeLoanSections, // LAP uses same structure as home loan
      requiredDocuments: ['PAN Card', 'Aadhar Card', 'Bank Statements (6 months)', 'ITR (2 years)', 'Property Documents', 'Property Tax Receipt'],
    },
    {
      loanType: 'personal_loan',
      sections: personalLoanSections,
      requiredDocuments: ['PAN Card', 'Aadhar Card', 'Bank Statements (6 months)', 'Salary Slips (3 months) / ITR (2 years)'],
    },
    {
      loanType: 'business_loan',
      sections: businessLoanSections,
      requiredDocuments: ['PAN Card', 'Aadhar Card', 'Bank Statements (12 months)', 'ITR (2-3 years)', 'GST Returns', 'Business Proof', 'Financial Statements'],
    },
    {
      loanType: 'car_loan',
      sections: carLoanSections,
      requiredDocuments: ['PAN Card', 'Aadhar Card', 'Bank Statements (6 months)', 'Salary Slips (3 months) / ITR (2 years)', 'Proforma Invoice'],
    },
  ];

  const templatesCollection = collection(db, 'questionnaire_templates');

  for (const templateData of templates) {
    try {
      console.log(`📝 Creating template for ${getLoanTypeName(templateData.loanType)}...`);

      const template: QuestionnaireTemplate = {
        id: templateData.loanType,
        loanType: templateData.loanType,
        name: getLoanTypeName(templateData.loanType),
        description: `Base questionnaire template for ${getLoanTypeName(templateData.loanType)}`,
        sections: templateData.sections,
        requiredDocuments: templateData.requiredDocuments,
        version: 1,
        isActive: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        createdBy: 'migration_script',
      };

      const docRef = doc(templatesCollection, templateData.loanType);
      await setDoc(docRef, template);

      result.templatesCreated.push(templateData.loanType);
      console.log(`  ✅ ${getLoanTypeName(templateData.loanType)} created successfully`);
    } catch (error) {
      result.success = false;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      result.errors.push({ loanType: templateData.loanType, error: errorMessage });
      console.error(`  ❌ Error creating ${templateData.loanType}:`, errorMessage);
    }
  }

  console.log('\n📊 Migration Summary:');
  console.log(`  Templates created: ${result.templatesCreated.length}/5`);
  if (result.errors.length > 0) {
    console.log(`  Errors: ${result.errors.length}`);
    result.errors.forEach(e => console.log(`    - ${e.loanType}: ${e.error}`));
  }
  console.log('\n✨ Migration complete!');

  return result;
};

/**
 * Check if templates already exist
 */
export const checkExistingTemplates = async (): Promise<string[]> => {
  const templatesCollection = collection(db, 'questionnaire_templates');
  const snapshot = await getDocs(templatesCollection);
  return snapshot.docs.map(doc => doc.id);
};

/**
 * Delete all templates (use with caution!)
 */
export const clearTemplates = async (): Promise<void> => {
  const templatesCollection = collection(db, 'questionnaire_templates');
  const snapshot = await getDocs(templatesCollection);
  
  console.log(`🗑️ Deleting ${snapshot.size} templates...`);
  
  // Note: In a real app, you'd use batch delete
  // For now, we just log - actual deletion should be done manually if needed
  console.log('Templates to delete:', snapshot.docs.map(d => d.id).join(', '));
  console.log('⚠️ Please delete templates manually from Firebase Console if needed.');
};

// Export for use in browser console
if (typeof window !== 'undefined') {
  (window as unknown as { runQuestionnaireMigration: typeof runMigration }).runQuestionnaireMigration = runMigration;
  (window as unknown as { checkExistingTemplates: typeof checkExistingTemplates }).checkExistingTemplates = checkExistingTemplates;
  (window as unknown as { migrateQuestionnaireTemplates: typeof runMigration }).migrateQuestionnaireTemplates = runMigration;
}

// Export alias for use in QuestionnaireManagement component
export const migrateQuestionnaireTemplates = runMigration;
